import { useParams, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useSEO } from "../components/SEO";
import { Reveal, SectionTag } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { JsonLd } from "../components/JsonLd";
import { serviceSchema, faqSchema } from "../lib/schema";
import { BookCallButton, BlueprintButton, ArrowLink } from "../components/CTA";
import { CtaBand } from "../components/CtaBand";
import { getServiceBySlug } from "../data/servicePages";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useSEO(
    service ? service.seoTitle : "Services | Nexus Catalyst Agency",
    service ? service.seoDesc : "Hajj and Umrah marketing services for Indian travel agencies."
  );

  if (!service) return <Navigate to="/services" replace />;

  return (
    <main data-testid={`service-detail-${service.slug}`}>
      <JsonLd
        data={serviceSchema({
          name: service.h1,
          description: service.seoDesc,
          path: service.path,
        })}
      />
      <JsonLd data={faqSchema(service.faqs)} />

      <section className="relative bg-cream pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden" data-testid="service-detail-hero">
        <div className="absolute inset-0 geo-pattern opacity-[0.05]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Reveal>
            <Breadcrumbs
              items={[
                { label: "Services", to: "/services" },
                { label: service.title, to: service.path },
              ]}
            />
            <SectionTag className="mb-7">{service.heroTag}</SectionTag>
          </Reveal>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-charcoal leading-[1.03] max-w-4xl">
            {service.h1}
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-charcoal/65 leading-relaxed">
            {service.heroCopy}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <BookCallButton testid="service-detail-book-call" />
            <BlueprintButton testid="service-detail-audit" />
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-14">
            {service.points.map((p, i) => (
              <Reveal key={p.heading}>
                <div data-testid={`service-point-${i + 1}`}>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-charcoal">
                    {p.heading}
                  </h2>
                  {p.body.map((para, j) => (
                    <p key={j} className="mt-4 text-base text-charcoal/65 leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal>
                <div className="bg-cream border border-charcoal/10 p-8">
                  <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-5">
                    What's Included
                  </div>
                  <ul className="space-y-3">
                    {service.includes.map((inc) => (
                      <li key={inc} className="flex items-center gap-3">
                        <Check size={15} strokeWidth={2} className="text-gold shrink-0" />
                        <span className="text-sm font-medium text-charcoal/75">{inc}</span>
                      </li>
                    ))}
                  </ul>
                  {service.note && (
                    <p className="mt-6 text-xs text-charcoal/50 leading-relaxed border-t border-charcoal/10 pt-5">
                      {service.note}
                    </p>
                  )}
                </div>
              </Reveal>
              {service.keywords && (
                <Reveal delay={0.1}>
                  <div className="bg-cream border border-charcoal/10 p-8">
                    <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-4">
                      Example Search Intent
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {service.keywords.map((k) => (
                        <span key={k} className="border border-charcoal/15 bg-white px-3 py-1.5 text-[11px] font-medium text-charcoal/65">
                          {k}
                        </span>
                      ))}
                    </div>
                    <p className="mt-4 text-xs text-charcoal/50 leading-relaxed">
                      We validate real search demand and competition before
                      recommending budgets. No invented numbers.
                    </p>
                  </div>
                </Reveal>
              )}
              {service.emailFlow && (
                <Reveal delay={0.1}>
                  <div className="bg-cream border border-charcoal/10 p-8">
                    <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-4">
                      A Typical Nurture Sequence
                    </div>
                    <div className="flex flex-wrap items-center gap-y-3">
                      {service.emailFlow.map((step, j) => (
                        <div key={step} className="flex items-center">
                          <span className="border border-gold/40 bg-white px-3 py-1.5 text-[11px] font-semibold text-charcoal/70">
                            {step}
                          </span>
                          {j < service.emailFlow.length - 1 && (
                            <span className="mx-2 text-gold text-xs">→</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream py-20 md:py-28" data-testid="service-detail-faq">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <SectionTag className="mb-6">Common Questions</SectionTag>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-charcoal mb-10">
              {service.title}: Questions Agencies Ask
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-charcoal/10">
                  <AccordionTrigger className="text-left text-base md:text-lg font-semibold tracking-tight text-charcoal hover:text-tan hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-charcoal/65 leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <div className="border border-gold/30 bg-white p-6 md:p-8">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-3">
                Further Reading
              </div>
              <div className="flex flex-col gap-2">
                {service.relatedArticles.map((slug) => (
                  <ArrowLink key={slug} to={`/resources/${slug}`} testid={`service-related-${slug}`}>
                    {slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </ArrowLink>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={`Ready to Put ${service.title} to Work for Your Agency?`}
        copy="Book a strategy call and we will map this service to your market, your packages and your season."
        testid="service-detail-cta-band"
      />
    </main>
  );
}
